import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, IndianRupee, PackageCheck, FileText, ClipboardList } from "lucide-react";
import { useVendorAuth, vendorFetch } from "./VendorAuthContext";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}-${m}-${y}` : iso;
};

const STAT_CARDS = [
  { key: "open_pos", label: "Open Purchase Orders", icon: ShoppingCart, color: "bg-sky-100 text-sky-700", format: (v) => v },
  { key: "pending_po_value", label: "Pending PO Value", icon: IndianRupee, color: "bg-emerald-100 text-emerald-700", format: (v) => `₹ ${inr(v)}` },
  { key: "pending_po_qty", label: "Pending PO Quantity", icon: PackageCheck, color: "bg-violet-100 text-violet-700", format: (v) => inr(v) },
  { key: "invoices_submitted", label: "Invoices Submitted", icon: FileText, color: "bg-amber-100 text-amber-700", format: (v) => v },
  { key: "invoices_pending_approval", label: "Invoices Pending Approval", icon: ClipboardList, color: "bg-orange-100 text-orange-700", format: (v) => v },
];

export default function VendorDashboard() {
  const { token, vendor } = useVendorAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    vendorFetch(token)("/api/vendor/dashboard")
      .then((r) => r.ok ? r.json() : Promise.reject(new Error("Failed to load dashboard")))
      .then(setData)
      .catch((e) => setError(e.message));
  }, [token]);

  return (
    <div className="space-y-6" data-testid="vendor-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-black text-[#0a2a5e]">Welcome, {vendor?.name || "Vendor"}</h1>
        <p className="text-sm text-slate-500 mt-1">Here's a snapshot of your account with BAISHALII.</p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {STAT_CARDS.map(({ key, label, icon: Icon, color, format }) => (
          <div key={key} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-5" data-testid={`stat-${key}`}>
            <div className={`inline-flex items-center justify-center h-9 w-9 rounded-lg ${color} mb-3`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-xl md:text-2xl font-bold text-slate-800 mt-1">
              {data ? format(data[key]) : "…"}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800">Recent Purchase Orders</h2>
          <Link to="/vendor/pos" className="text-sm text-[#0a2a5e] hover:underline">View all →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-semibold">PO Number</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold text-right">Balance Value</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!data && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
              {data?.recent_pos?.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No open purchase orders yet.</td></tr>}
              {data?.recent_pos?.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-[#0a2a5e]">{p.po_number}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(p.po_date)}</td>
                  <td className="px-4 py-3 text-right font-semibold">₹ {inr(p.summary?.balance_value)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">{p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/vendor/pos/${p.id}`} className="text-[#0a2a5e] text-sm font-semibold hover:underline">View</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
